import { Drawer as DrawerPrimitive } from "@base-ui/react/drawer";
import type { ComponentProps, CSSProperties, ReactNode } from "react";
import { cn } from "../../lib/utils.ts";
import { BOTTOM_SHEET_HANDLE_CLASS_NAME } from "./bottom-sheet-handle.ts";
import { closeTileClassName } from "./close-tile.ts";
import { edgePanelRef, edgeScrimRef } from "./browser-edges.tsx";
import { EDGE_COLORS, edgeScrim } from "./edge-color.ts";
import { iconButtonClass } from "./icon-button-class.ts";

function Drawer({
    children,
    ...props
}: Omit<DrawerPrimitive.Root.Props, "children"> & {
    children?: ReactNode;
}) {
    return (
        <DrawerPrimitive.Root {...props}>
            <DrawerPrimitive.VirtualKeyboardProvider>
                {children}
            </DrawerPrimitive.VirtualKeyboardProvider>
        </DrawerPrimitive.Root>
    );
}

function DrawerTrigger({
    ...props
}: ComponentProps<typeof DrawerPrimitive.Trigger>) {
    return <DrawerPrimitive.Trigger data-slot="drawer-trigger" {...props} />;
}

const CLOSE_VARIANT_CLASS_NAME = {
    default: "",
    tile: closeTileClassName,
    icon: cn(
        iconButtonClass,
        "text-doberman-black [@media(hover:hover)]:hover:bg-true-black/8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-labrador-yellow",
    ),
};

function DrawerClose({
    className,
    variant = "default",
    ...props
}: ComponentProps<typeof DrawerPrimitive.Close> & {
    variant?: keyof typeof CLOSE_VARIANT_CLASS_NAME;
}) {
    return (
        <DrawerPrimitive.Close
            data-slot="drawer-close"
            className={cn(CLOSE_VARIANT_CLASS_NAME[variant], className)}
            {...props}
        />
    );
}

const SHEET_SWIPE_TRANSFORM_CLASS_NAME =
    "[transform:translateY(calc(var(--drawer-snap-point-offset,0px)+var(--drawer-swipe-movement-y,0px)))] transition-transform";

const DOCKED_SHEET_PEEK_HEIGHT = 72;

const DOCKED_SHEET_PEEK_STYLE = {
    "--docked-sheet-peek": `${DOCKED_SHEET_PEEK_HEIGHT}px`,
} as CSSProperties;

const SHEET_CLOSED_CLASS_NAME = {
    offscreen:
        "data-ending-style:[transform:translateY(100%)] data-starting-style:[transform:translateY(100%)]",
    docked: "after:absolute after:inset-x-0 after:top-full after:h-10 after:bg-inherit data-ending-style:[transform:translateY(calc(100%-var(--docked-sheet-peek)-env(safe-area-inset-bottom)))] data-starting-style:[transform:translateY(calc(100%-var(--docked-sheet-peek)-env(safe-area-inset-bottom)))]",
};

const SHEET_MOTION_CLASS_NAME =
    "duration-500 ease-sheet-rise data-swiping:duration-0";

const DRAWER_SCRIMS = {
    default: edgeScrim("bg-true-black/40"),
    light: edgeScrim("bg-true-black/20"),
    strong: edgeScrim("bg-true-black/50"),
};

type DrawerContentProps = Omit<
    ComponentProps<typeof DrawerPrimitive.Popup>,
    "title" | "className"
> & {
    title: ReactNode;
    surface?: "default" | "maltese";
    scrim?: keyof typeof DRAWER_SCRIMS;
    elevated?: boolean;
    closed?: keyof typeof SHEET_CLOSED_CLASS_NAME;
    className?: string;
    layerClassName?: string;
    overlayClassName?: string;
    showHandle?: boolean;
};

function DrawerContent({
    className,
    children,
    title,
    surface = "default",
    scrim = "default",
    elevated = false,
    closed = "offscreen",
    layerClassName,
    overlayClassName,
    showHandle = true,
    ...props
}: DrawerContentProps) {
    return (
        <DrawerPrimitive.Portal data-slot="drawer-portal">
            <DrawerPrimitive.Backdrop
                data-slot="drawer-overlay"
                ref={edgeScrimRef(DRAWER_SCRIMS[scrim].tint)}
                className={cn(
                    "fixed inset-0 z-50 opacity-[calc(1-var(--drawer-swipe-progress))] transition-opacity data-ending-style:opacity-0 data-starting-style:opacity-0",
                    DRAWER_SCRIMS[scrim].className,
                    SHEET_MOTION_CLASS_NAME,
                    layerClassName,
                    overlayClassName,
                )}
            />
            <DrawerPrimitive.Viewport
                data-slot="drawer-viewport"
                className={cn("fixed inset-0 z-50", layerClassName)}
            >
                <DrawerPrimitive.Popup
                    data-slot="drawer-content"
                    ref={edgePanelRef(
                        surface === "maltese"
                            ? EDGE_COLORS.malteseWhite
                            : EDGE_COLORS.trueWhite,
                    )}
                    className={cn(
                        "fixed inset-x-0 bottom-0 flex flex-col rounded-t-sheet bg-true-white outline-none",
                        SHEET_SWIPE_TRANSFORM_CLASS_NAME,
                        SHEET_CLOSED_CLASS_NAME[closed],
                        SHEET_MOTION_CLASS_NAME,
                        elevated && "shadow-sheet-up",
                        surface === "maltese" && "bg-maltese-white",
                        className,
                    )}
                    {...props}
                    style={
                        closed === "docked"
                            ? { ...DOCKED_SHEET_PEEK_STYLE, ...props.style }
                            : props.style
                    }
                >
                    {showHandle ? <DrawerHandle /> : null}
                    <DrawerPrimitive.Title
                        data-slot="drawer-title"
                        className="sr-only"
                    >
                        {title}
                    </DrawerPrimitive.Title>
                    {children}
                </DrawerPrimitive.Popup>
            </DrawerPrimitive.Viewport>
        </DrawerPrimitive.Portal>
    );
}

function DrawerHandle({
    className,
    tone = "default",
    ...props
}: ComponentProps<"div"> & { tone?: "default" | "subtle" }) {
    return (
        <div
            aria-hidden
            data-slot="drawer-handle"
            className={cn(
                "mx-auto mt-3 mb-2 shrink-0",
                BOTTOM_SHEET_HANDLE_CLASS_NAME,
                tone === "subtle" && "bg-doberman-black/15",
                className,
            )}
            {...props}
        />
    );
}

export {
    DOCKED_SHEET_PEEK_HEIGHT,
    Drawer,
    DrawerClose,
    DrawerContent,
    DrawerHandle,
    DrawerTrigger,
};
